// src/utils/lpTracking.js
// Eigen event-tracking voor landingspagina's. Events gaan (gebatcht) naar het
// floawch-backend (hl-project.nl) en zijn daar uit te lezen via het dashboard
// op /online-coaching/dashboard. Volledig anoniem: random sessie-/client-id's,
// geen e-mailadressen of andere PII in de events.

const TRACK_URL = 'https://hl-project.nl/api/public/lp-track';
const FLUSH_DELAY_MS = 1500;
const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

const isDev = () =>
  window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok');

const uuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getId = (storage, key) => {
  try {
    let id = storage.getItem(key);
    if (!id) {
      id = uuid();
      storage.setItem(key, id);
    }
    return id;
  } catch {
    return uuid();
  }
};

let sessionId = null;
let clientId = null;
let currentPage = null;
let queue = [];
let flushTimer = null;

const flush = (useKeepalive = false) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  if (isDev()) return;
  // text/plain = CORS "simple request" (geen preflight) — cruciaal voor
  // exit-events bij het sluiten van de tab; de server parset de JSON zelf.
  fetch(TRACK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ events }),
    keepalive: useKeepalive
  }).catch(() => {});
};

export const trackLp = (event, { location = null, value = null, meta = {} } = {}) => {
  if (!sessionId) return;
  queue.push({
    sessionId,
    clientId,
    event,
    page: currentPage,
    location,
    value,
    meta
  });
  // Batch: kort wachten zodat snelle opeenvolgende events in één request gaan
  if (!flushTimer) flushTimer = setTimeout(() => flush(), FLUSH_DELAY_MS);
};

const getSource = () => {
  const utmSource = new URLSearchParams(window.location.search).get('utm_source');
  if (utmSource) return utmSource.slice(0, 64);
  if (document.referrer) {
    try {
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      if (host && host !== window.location.hostname.replace(/^www\./, '')) return host;
      return 'intern';
    } catch {
      return 'direct';
    }
  }
  return 'direct';
};

const getScrollPct = () => {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return 100;
  return Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100));
};

// Start alle pagina-tracking; geeft een cleanup-functie terug voor unmount.
export const initLpTracking = (page) => {
  sessionId = getId(window.sessionStorage, 'lp_sid');
  clientId = getId(window.localStorage, 'lp_cid');
  currentPage = page;

  const params = new URLSearchParams(window.location.search);
  trackLp('page_view', {
    meta: {
      source: getSource(),
      referrer: document.referrer ? document.referrer.slice(0, 200) : null,
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      fbclid: params.get('fbclid') ? true : undefined,
      device: window.innerWidth < 768 ? 'mobiel' : 'desktop',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      lang: navigator.language
    }
  });

  // --- Scroll-diepte (milestones, elk één keer per sessie) -----------------
  const firedMilestones = new Set();
  let maxScroll = 0;
  const onScroll = () => {
    const pct = getScrollPct();
    if (pct > maxScroll) maxScroll = pct;
    SCROLL_MILESTONES.forEach((m) => {
      if (pct >= m && !firedMilestones.has(m)) {
        firedMilestones.add(m);
        trackLp('scroll_depth', { value: m });
      }
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // --- Tijd op pagina (alleen zichtbare tijd telt) --------------------------
  let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
  let visibleSecs = 0;
  const accumulate = () => {
    if (visibleSince) {
      visibleSecs += (Date.now() - visibleSince) / 1000;
      visibleSince = null;
    }
  };

  // Bij verbergen/verlaten: exit-event met tijd + diepste scrollpunt.
  // Kan meerdere keren vuren (tab-switch en terug); het dashboard neemt het
  // maximum per sessie, dus dat is veilig.
  const sendExit = () => {
    accumulate();
    trackLp('page_exit', {
      value: Math.round(visibleSecs),
      meta: { max_scroll: maxScroll }
    });
    flush(true);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      sendExit();
    } else if (!visibleSince) {
      visibleSince = Date.now();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', sendExit);

  // --- Sectie-weergaven (elementen met data-lp-section) ---------------------
  const seenSections = new Set();
  const sectionEls = document.querySelectorAll('[data-lp-section]');
  const sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const name = entry.target.getAttribute('data-lp-section');
        if (entry.isIntersecting && name && !seenSections.has(name)) {
          seenSections.add(name);
          trackLp('section_view', { location: name });
          sectionObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.25 }
  );
  sectionEls.forEach((el) => sectionObserver.observe(el));

  return () => {
    sendExit();
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', sendExit);
    sectionObserver.disconnect();
    sessionId = null;
    currentPage = null;
  };
};

// Voegt <meta name="robots" content="noindex, nofollow"> toe zolang de pagina
// gemount is — houdt landingspagina's en het dashboard uit Google.
export const applyNoIndex = () => {
  const existing = document.querySelector('meta[name="robots"]');
  if (existing) return () => {};
  const meta = document.createElement('meta');
  meta.name = 'robots';
  meta.content = 'noindex, nofollow, noimageindex';
  document.head.appendChild(meta);
  return () => {
    document.head.removeChild(meta);
  };
};
